import { useEffect, useRef, useState } from 'react';
import { Play, Square, Trash2, Zap, ChevronRight, Radio } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { apiFetch, webhookListenUrl } from '@/lib/api';

interface InspectorEvent { key: number; type: string; at: string; payload: unknown }

const TRIGGER_OPTIONS = [
  'message.received', 'message.delivered', 'message.read',
  'group.participant_added', 'group.subject_changed', 'instance.connected',
];

// No oficial não há grupo nem conexão por QR: os eventos que importam são os da
// mensagem (inclusive a falha, com o código da Meta) e as decisões da Meta sobre
// template, número e conta. O servidor monta o exemplo real de cada um.
const OFFICIAL_TRIGGER_OPTIONS = [
  'message.received', 'message.sent', 'message.delivered', 'message.read', 'message.failed',
  'template.approved', 'template.rejected', 'template.quality_changed',
  'instance.quality_changed', 'instance.limit_changed', 'account.status_changed', 'account.alert',
  'contact.opted_out', 'contact.opted_in',
];

/**
 * Inspector ao vivo: escuta os EVENTOS DE WEBHOOK do projeto ativo (SSE) e exibe
 * cada evento + payload — sem precisar de URL pública. Liga no Play.
 */
export function WebhookInspector({ fullHeight = false, official = false }: { fullHeight?: boolean; official?: boolean }) {
  const options = official ? OFFICIAL_TRIGGER_OPTIONS : TRIGGER_OPTIONS;
  const [listening, setListening] = useState(false);
  const [events, setEvents] = useState<InspectorEvent[]>([]);
  const [open, setOpen] = useState<number | null>(null);
  const [triggerType, setTriggerType] = useState(options[0]);
  const esRef = useRef<EventSource | null>(null);
  const seq = useRef(0);

  useEffect(() => () => esRef.current?.close(), []);

  const start = () => {
    if (esRef.current) return;
    const es = new EventSource(webhookListenUrl());
    esRef.current = es;
    es.addEventListener('webhook.event', (e) => {
      let payload: unknown = null;
      try { payload = JSON.parse((e as MessageEvent).data); } catch { payload = (e as MessageEvent).data; }
      const type = (payload as { event_type?: string })?.event_type ?? 'event';
      setEvents((prev) => [{ key: ++seq.current, type, at: new Date().toLocaleTimeString(), payload }, ...prev].slice(0, 100));
    });
    setListening(true);
  };
  const stop = () => { esRef.current?.close(); esRef.current = null; setListening(false); };
  const trigger = async () => { await apiFetch('POST', '/webhooks/trigger', { event_type: triggerType }); };

  return (
    <Card className={fullHeight ? 'flex h-full flex-col' : undefined}>
      <CardContent className={'flex flex-col gap-4 p-5' + (fullHeight ? ' h-full min-h-0' : '')}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="flex items-center gap-2 text-base font-semibold">
              <Radio className={'size-4 ' + (listening ? 'text-emerald-500' : 'text-muted-foreground')} />
              Inspector de webhooks (ao vivo)
            </h2>
            <p className="text-sm text-muted-foreground">Escuta os eventos do projeto e mostra os payloads em tempo real — sem expor URL.</p>
          </div>
          <div className="flex items-center gap-2">
            {!listening ? (
              <Button onClick={start}><Play className="size-4" /> Escutar</Button>
            ) : (
              <Button variant="outline" onClick={stop}><Square className="size-4" /> Parar</Button>
            )}
            {events.length > 0 && (
              <Button variant="ghost" size="sm" onClick={() => { setEvents([]); setOpen(null); }}><Trash2 className="size-4" /></Button>
            )}
          </div>
        </div>

        {/* Disparar evento de teste */}
        <div className="flex flex-wrap items-center gap-2 rounded-md border bg-muted/30 p-2">
          <span className="text-xs text-muted-foreground">Sem atividade real? Dispare um teste:</span>
          <select value={triggerType} onChange={(e) => setTriggerType(e.target.value)} className="h-8 rounded-md border bg-transparent px-2 text-sm">
            {options.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
          <Button size="sm" variant="outline" onClick={trigger}><Zap className="size-3.5" /> Disparar</Button>
        </div>

        {/* Lista de eventos */}
        <div className={fullHeight ? 'flex-1 min-h-0 overflow-y-auto' : ''}>
        {!listening && events.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">Clique em <strong>Escutar</strong> e os eventos do projeto aparecem aqui.</p>
        ) : events.length === 0 ? (
          <p className="flex items-center justify-center gap-2 py-8 text-center text-sm text-muted-foreground">
            <span className="size-2 animate-pulse rounded-full bg-emerald-500" /> Escutando… faça algo (ou dispare um teste).
          </p>
        ) : (
          <ul className="flex flex-col divide-y rounded-md border">
            {events.map((ev) => (
              <li key={ev.key}>
                <button onClick={() => setOpen(open === ev.key ? null : ev.key)} className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-accent/50">
                  <ChevronRight className={'size-4 shrink-0 text-muted-foreground transition-transform ' + (open === ev.key ? 'rotate-90' : '')} />
                  <Badge variant="secondary" className="font-mono">{ev.type}</Badge>
                  <span className="ml-auto text-xs text-muted-foreground">{ev.at}</span>
                </button>
                {open === ev.key && (
                  <pre className="overflow-x-auto border-t bg-muted/30 px-4 py-3 text-xs leading-relaxed"><code>{JSON.stringify(ev.payload, null, 2)}</code></pre>
                )}
              </li>
            ))}
          </ul>
        )}
        </div>
      </CardContent>
    </Card>
  );
}
